import { createElement, Languages } from "lucide";

type TextField = HTMLInputElement | HTMLTextAreaElement;
type TranslationContext = {
  locale: string;
  scope: () => string;
  translate: (text: string) => Promise<string>;
};

export function englishFieldKey(arabicKey: string): string | undefined {
  return /^[a-zA-Z]+Ar$/.test(arabicKey) ? arabicKey.slice(0, -2) : undefined;
}

/** Only explicitly paired editorial fields participate; never arbitrary user/account text. */
export function enhanceArabicTranslation(root: HTMLElement, context: TranslationContext): void {
  const ar = context.locale === "ar";
  const t = (en: string, arabic: string) => (ar ? arabic : en);
  const fields = [...root.querySelectorAll<TextField>('input:not([type="hidden"]), textarea')];
  const forms = new Map<HTMLFormElement, Array<{ field: TextField; run: () => Promise<void> }>>();
  for (const target of fields) {
    if (target.dataset.translationEnhanced) continue;
    const attr = [...target.attributes].find(
      (item) =>
        (item.name === "name" || /^data-.+-field$/.test(item.name)) && englishFieldKey(item.value),
    );
    if (!attr) continue;
    const key = englishFieldKey(attr.value)!;
    const form = target.closest("form");
    const source = fields.find(
      (field) =>
        field.closest("form") === form &&
        (field.getAttribute(attr.name) === key || field.getAttribute(attr.name) === `${key}En`),
    );
    if (!form || !source) continue;
    target.dataset.translationEnhanced = "true";
    target.required = false;
    target.removeAttribute("aria-required");
    target.dir = "rtl";
    target.lang = "ar";
    source.lang = "en";
    const label = target.closest("label");
    const optional = document.createElement("small");
    optional.className = "translation-optional";
    optional.textContent = t("Optional", "اختياري");
    label?.insertBefore(optional, target);
    const controls = document.createElement("div");
    controls.className = "translation-controls";
    const button = document.createElement("button");
    button.type = "button";
    button.className = "secondary-action translation-button";
    const caption = document.createElement("span");
    caption.textContent = t("Translate from English", "ترجمة من الإنجليزية");
    button.append(
      createElement(Languages, { "aria-hidden": "true", width: 16, height: 16 }),
      caption,
    );
    button.title = caption.textContent;
    const fieldLabel = [...(label?.childNodes ?? [])]
      .filter((node) => node.nodeType === Node.TEXT_NODE)
      .map((node) => node.textContent?.trim())
      .filter(Boolean)
      .join(" ");
    button.setAttribute("aria-label", `${caption.textContent}: ${fieldLabel}`);
    const status = document.createElement("span");
    status.className = "translation-status";
    status.setAttribute("role", "status");
    const replace = document.createElement("button");
    replace.type = "button";
    replace.className = "secondary-action";
    replace.textContent = t("Replace Arabic", "استبدال النص العربي");
    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.className = "secondary-action";
    cancel.textContent = t("Cancel", "إلغاء");
    replace.hidden = cancel.hidden = true;
    controls.append(button, status, replace, cancel);
    // Keep buttons outside the field label so clicking one cannot focus/submit the input.
    if (label) {
      const wrapper = document.createElement("div");
      wrapper.className = "translation-field";
      label.before(wrapper);
      wrapper.append(label, controls);
    } else target.after(controls);
    let busy = false;
    let revision = 0;
    const refresh = () => {
      button.disabled = busy || target.disabled || target.readOnly || !source.value.trim();
    };
    for (const field of [source, target])
      field.addEventListener("input", () => {
        revision += 1;
        replace.hidden = cancel.hidden = true;
        status.textContent = "";
        refresh();
      });
    const run = async () => {
      if (busy || target.disabled || target.readOnly || !source.value.trim()) return;
      const snapshot = {
        source: source.value,
        target: target.value,
        scope: context.scope(),
        revision,
      };
      busy = true;
      refresh();
      replace.hidden = cancel.hidden = true;
      status.textContent = t("Translating…", "جارٍ الترجمة…");
      try {
        const text = await context.translate(snapshot.source.trim());
        if (!target.isConnected || !source.isConnected || context.scope() !== snapshot.scope)
          return;
        if (
          revision !== snapshot.revision ||
          source.value !== snapshot.source ||
          target.value !== snapshot.target
        ) {
          status.textContent = t(
            "Text changed. Translate again when ready.",
            "تغيّر النص. أعد الترجمة عندما تكون جاهزًا.",
          );
          return;
        }
        if (!text.trim()) throw new Error("translation_failed");
        if (target.maxLength > 0 && text.length > target.maxLength) {
          status.textContent = t(
            "Translation is too long. Shorten the English text and retry.",
            "الترجمة طويلة جدًا. اختصر النص الإنجليزي وأعد المحاولة.",
          );
          return;
        }
        target.value = text;
        target.dispatchEvent(new Event("input", { bubbles: true }));
        status.textContent = t(
          "Translated. Review before saving.",
          "تمت الترجمة. راجع النص قبل الحفظ.",
        );
      } catch (error) {
        if (!target.isConnected || context.scope() !== snapshot.scope) return;
        status.textContent =
          error instanceof Error && error.message === "translation_rate_limited"
            ? t(
                "Translation limit reached. Try again later, or enter Arabic manually.",
                "تم بلوغ حد الترجمة. حاول لاحقًا أو أدخل النص العربي يدويًا.",
              )
            : t(
                "Translation unavailable. Retry, enter Arabic manually, or leave it empty.",
                "الترجمة غير متاحة. أعد المحاولة أو أدخل العربية يدويًا أو اتركها فارغة.",
              );
      } finally {
        busy = false;
        refresh();
      }
    };
    button.addEventListener("click", () => {
      if (!target.value.trim()) void run();
      else {
        status.textContent = t(
          "Replace the existing Arabic text?",
          "هل تريد استبدال النص العربي الحالي؟",
        );
        replace.hidden = cancel.hidden = false;
      }
    });
    replace.addEventListener("click", () => void run());
    cancel.addEventListener("click", () => {
      replace.hidden = cancel.hidden = true;
      status.textContent = "";
      button.focus();
    });
    refresh();
    const pairs = forms.get(form) ?? [];
    pairs.push({ field: target, run });
    forms.set(form, pairs);
  }
  for (const [form, pairs] of forms) {
    if (pairs.length < 2 || form.querySelector(".translation-all")) continue;
    const button = document.createElement("button");
    button.type = "button";
    button.className = "secondary-action translation-all";
    button.append(
      createElement(Languages, { "aria-hidden": "true", width: 16, height: 16 }),
      document.createTextNode(t("Translate empty Arabic fields", "ترجمة الحقول العربية الفارغة")),
    );
    button.addEventListener("click", async () => {
      button.disabled = true;
      const scope = context.scope();
      try {
        for (const pair of pairs) {
          if (!form.isConnected || scope !== context.scope()) break;
          if (!pair.field.value.trim()) await pair.run();
        }
      } finally {
        button.disabled = false;
      }
    });
    form.prepend(button);
  }
}
